import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import ActiveStatusCell from "../../../utils/ActiveStatusCell";
import RowCategoryActionsMenu from "./RowCategoryActionsMenu";
// Inlined TypesManagement and RowTypesActionsMenu below

export default function ActivityCategoriesManagement() {
  const [openTypesFor, setOpenTypesFor] = useState(null);
  const [createTypeDialogOpen, setCreateTypeDialogOpen] = useState(false);
  const [createTypeCategory, setCreateTypeCategory] = useState(null);
  const [createTypeForm, setCreateTypeForm] = useState({ type_name: "", is_active: 1 });
  // Types management state
  const [typesRows, setTypesRows] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesDialogOpen, setTypesDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({ type_name: "", is_active: 1 });
  const [typesSnackbarOpen, setTypesSnackbarOpen] = useState(false);
  const [typesSnackbarMessage, setTypesSnackbarMessage] = useState("");
  const [typesSnackbarSeverity, setTypesSnackbarSeverity] = useState("success");
  const [typesAnchorEl, setTypesAnchorEl] = useState(null);
  const [typesMenuRow, setTypesMenuRow] = useState(null);
  // Fix: Add missing handleActionsClick handler
  const handleActionsClick = (e) => {
    setActionsAnchorEl(e.currentTarget);
  };

  // Handler for Create Activity Type action
  const handleCreateType = (category) => {
    setCreateTypeCategory(category);
    setCreateTypeForm({ type_name: "", is_active: 1 });
    setCreateTypeDialogOpen(true);
  };

  const handleCreateTypeSubmit = async () => {
    if (!createTypeForm.type_name.trim()) {
      showTypesSnackbar("Type name is required", "error");
      return;
    }
    try {
      const url = `${API_BASE_URL}/api/activitytypes/${Number(user.client_id) || 1}/${
        Number(user.college_id) || 1
      }/${createTypeCategory.activity_category_id}`;
      const payload = {
        activity_type: createTypeForm.type_name,
        is_active: createTypeForm.is_active,
        client_id: user.client_id,
      };
      await axios.post(url, payload, { headers: getAuthHeaders() });
      showTypesSnackbar("Activity type created", "success");
      setCreateTypeDialogOpen(false);
      setCreateTypeCategory(null);
      setCreateTypeForm({ type_name: "", is_active: 1 });
      // Optionally refresh types list if viewing
      if (
        openTypesFor &&
        openTypesFor.activity_category_id === createTypeCategory.activity_category_id
      ) {
        fetchTypes(openTypesFor);
      }
    } catch (err) {
      showTypesSnackbar(err?.response?.data?.message || "Error creating activity type", "error");
    }
  };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const openActionsMenu = Boolean(actionsAnchorEl);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const user = useMemo(() => {
    const u = sessionStorage.getItem("user");
    return u ? JSON.parse(u) : null;
  }, []);

  // Types management helpers
  const showTypesSnackbar = (msg, sev = "success") => {
    setTypesSnackbarMessage(msg);
    setTypesSnackbarSeverity(sev);
    setTypesSnackbarOpen(true);
  };

  const fetchTypes = useCallback(
    async (category) => {
      if (!user || !category) return;
      setTypesLoading(true);
      try {
        const url = `${API_BASE_URL}/api/activitytypes/${Number(user.client_id) || 1}/${
          Number(user.college_id) || 1
        }/${category.activity_category_id}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        let data = [];
        if (Array.isArray(res.data)) data = res.data;
        else if (res.data && Array.isArray(res.data.data)) data = res.data.data;
        setTypesRows(data || []);
      } catch (err) {
        showTypesSnackbar("Failed to load activity types", "error");
        setTypesRows([]);
      } finally {
        setTypesLoading(false);
      }
    },
    [user, API_BASE_URL]
  );

  const handleOpenTypesDialog = (category) => {
    setOpenTypesFor(category);
    setTypesDialogOpen(true);
    setEditingType(null);
    setTypeFormData({ type_name: "", is_active: 1 });
    fetchTypes(category);
  };

  const handleCloseTypesDialog = () => {
    setOpenTypesFor(null);
    setTypesDialogOpen(false);
    setEditingType(null);
    setTypeFormData({ type_name: "", is_active: 1 });
    setTypesRows([]);
  };

  const [editTypeDialogOpen, setEditTypeDialogOpen] = useState(false);
  const [editTypeForm, setEditTypeForm] = useState({ type_name: "", is_active: 1 });
  const [editTypeRow, setEditTypeRow] = useState(null);

  const handleEditType = (row) => {
    setEditTypeRow(row);
    setEditTypeForm({ type_name: row.activity_type || "", is_active: row.is_active || 1 });
    setEditTypeDialogOpen(true);
  };

  const handleEditTypeSubmit = async () => {
    if (!editTypeForm.type_name.trim()) {
      showTypesSnackbar("Type name is required", "error");
      return;
    }
    try {
      const url = `${API_BASE_URL}/api/activitytypes/${Number(user.client_id) || 1}/${
        Number(user.college_id) || 1
      }/${openTypesFor.activity_category_id}/${editTypeRow.activity_type_id}`;
      const payload = {
        type_name: editTypeForm.type_name,
        activity_type: editTypeForm.type_name,
        is_active: editTypeForm.is_active,
      };
      await axios.put(url, payload, { headers: getAuthHeaders() });
      showTypesSnackbar("Activity type updated", "success");
      setEditTypeDialogOpen(false);
      setEditTypeRow(null);
      setEditTypeForm({ type_name: "", is_active: 1 });
      fetchTypes(openTypesFor);
    } catch (err) {
      showTypesSnackbar(err?.response?.data?.message || "Error updating activity type", "error");
    }
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm("Are you sure you want to delete this activity type?")) return;
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/api/activitytypes/${Number(user.client_id) || 1}/${
          Number(user.college_id) || 1
        }/${openTypesFor.activity_category_id}/${id}`,
        { headers: getAuthHeaders() }
      );
      showTypesSnackbar(res.data.message || "Activity type deleted", "success");
      fetchTypes(openTypesFor);
    } catch (err) {
      showTypesSnackbar(err?.response?.data?.message || "Error deleting activity type", "error");
    }
  };

  const handleTypeSubmit = async () => {
    if (!typeFormData.type_name?.trim()) {
      showTypesSnackbar("Type name is required", "error");
      return;
    }
    try {
      let res;
      const payload = {
        activity_type: typeFormData.type_name,
        activity_category_id: openTypesFor.activity_category_id,
        college_id: user.college_id,
        is_active: typeFormData.is_active,
      };
      if (editingType) {
        res = await axios.put(
          `${API_BASE_URL}/api/activitytypes/${Number(user.client_id) || 1}/${
            Number(user.college_id) || 1
          }/${openTypesFor.activity_category_id}/${editingType.activity_type_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else {
        res = await axios.post(
          `${API_BASE_URL}/api/activitytypes/${Number(user.client_id) || 1}/${
            Number(user.college_id) || 1
          }/${openTypesFor.activity_category_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      }
      showTypesSnackbar(res.data.message || (editingType ? "Updated" : "Created"), "success");
      setEditingType(null);
      setTypeFormData({ type_name: "", is_active: 1 });
      fetchTypes(openTypesFor);
    } catch (err) {
      showTypesSnackbar(err?.response?.data?.message || "Error saving activity type", "error");
    }
  };

  const RowTypesActionsMenu = require("./RowTypesActionsMenu").default;
  const typesColumns = [
    { field: "activity_type_id", headerName: "ID", flex: 0.4, minWidth: 60 },
    { field: "activity_type", headerName: "Type Name", flex: 1.2, minWidth: 180 },
    {
      field: "is_active",
      headerName: "Active",
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <ActiveStatusCell
          value={params.row.is_active == 1 ? "ACTIVE" : "INACTIVE"}
          onClick={async () => {
            const newStatus = params.row.is_active == 1 ? 0 : 1;
            try {
              await axios.put(
                `${API_BASE_URL}/api/activitytypes/${Number(user.client_id) || 1}/${
                  Number(user.college_id) || 1
                }/${openTypesFor.activity_category_id}/${params.row.activity_type_id}`,
                { is_active: newStatus },
                { headers: getAuthHeaders() }
              );
              showTypesSnackbar("Status updated", "success");
              fetchTypes(openTypesFor);
            } catch (err) {
              showTypesSnackbar(err?.response?.data?.message || "Failed to update status", "error");
            }
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      flex: 0.8,
      minWidth: 60,
      renderCell: (params) => (
        <RowTypesActionsMenu row={params.row} onEdit={handleEditType} onDelete={handleDeleteType} />
      ),
    },
  ];
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/api/activitycategories/${user.client_id}/${user.college_id}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      let data = [];
      if (Array.isArray(res.data)) data = res.data;
      else if (res.data && Array.isArray(res.data.data)) data = res.data.data;
      setAllRows(data || []);
      setRows(data || []);
    } catch (err) {
      showSnackbar("Failed to load activity categories", "error");
      setAllRows([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  useEffect(() => {
    let filtered = [...allRows];
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter((r) => (r.activity_category || "").toLowerCase().includes(s));
    }
    setRows(filtered);
  }, [search, allRows]);

  const handleEdit = (row) => {
    setEditing(row);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this activity category?")) return;
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/api/activitycategories/${user.client_id}/${user.college_id}/${id}`,
        { headers: getAuthHeaders() }
      );
      showSnackbar(res.data.message || "Activity category deleted", "success");
      fetchData();
    } catch (err) {
      showSnackbar(err?.response?.data?.message || "Error deleting activity category", "error");
    }
  };

  const [categoryFormData, setCategoryFormData] = useState({
    category_name: "",
    is_active: 1,
  });

  useEffect(() => {
    if (editing) {
      setCategoryFormData({
        category_name: editing.activity_category || "",
        is_active: editing.is_active || 1,
      });
    } else {
      setCategoryFormData({ category_name: "", is_active: 1 });
    }
  }, [editing]);

  const handleSubmit = async () => {
    if (!categoryFormData.category_name?.trim()) {
      showSnackbar("Category name is required", "error");
      return;
    }
    try {
      let res;
      const payload = {
        activity_category: categoryFormData.category_name,
        college_id: user.college_id,
        is_active: categoryFormData.is_active,
      };
      if (editing) {
        res = await axios.put(
          `${API_BASE_URL}/api/activitycategories/${user.client_id}/${user.college_id}/${editing.activity_category_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else {
        res = await axios.post(
          `${API_BASE_URL}/api/activitycategories/${user.client_id}/${user.college_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      }
      showSnackbar(res.data.message || (editing ? "Updated" : "Created"), "success");
      setOpen(false);
      setEditing(null);
      fetchData();
    } catch (err) {
      showSnackbar(err?.response?.data?.message || "Error saving activity category", "error");
    }
  };

  const columns = [
    { field: "activity_category_id", headerName: "ID", flex: 0.4, minWidth: 60 },
    { field: "activity_category", headerName: "Category Name", flex: 1.2, minWidth: 180 },
    {
      field: "is_active",
      headerName: "Active",
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <ActiveStatusCell
          value={params.row.is_active == 1 ? "ACTIVE" : "INACTIVE"}
          onClick={async () => {
            const newStatus = params.row.is_active == 1 ? 0 : 1;
            try {
              await axios.put(
                `${API_BASE_URL}/api/activitycategories/${user.client_id}/${user.college_id}/${params.row.activity_category_id}`,
                { is_active: newStatus },
                { headers: getAuthHeaders() }
              );
              showSnackbar("Status updated", "success");
              fetchData();
            } catch (err) {
              showSnackbar(err?.response?.data?.message || "Failed to update status", "error");
            }
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      flex: 0.8,
      minWidth: 60,
      renderCell: (params) => (
        <RowCategoryActionsMenu
          row={params.row}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCreateType={handleCreateType}
          onViewTypes={handleOpenTypesDialog}
        />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            ACTIVITY CATEGORY MANAGEMENT
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{ color: "#fff", fontWeight: 500, letterSpacing: 1 }}
          >
            ADD ACTIVITY CATEGORY
          </Button>
        </Toolbar>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          sx={{ minHeight: 400 }}
          getRowId={(row) => row.activity_category_id}
        />
      </Paper>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editing ? "Edit Category" : "Add Category"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Category Name"
              required
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={categoryFormData.category_name}
              onChange={(e) =>
                setCategoryFormData({ ...categoryFormData, category_name: e.target.value })
              }
            />
            <TextField
              select
              margin="normal"
              label="Active"
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              value={categoryFormData.is_active}
              onChange={(e) =>
                setCategoryFormData({
                  ...categoryFormData,
                  is_active: Number(e.target.value),
                })
              }
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setEditing(null);
            }}
            variant="outlined"
            sx={{
              color: "#fff",
              backgroundColor: "#1976d2",
              borderColor: "#1976d2",
              fontWeight: 500,
              letterSpacing: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ ml: 2, color: "#fff", fontWeight: 500, letterSpacing: 1 }}
          >
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      {/* Create Activity Type Dialog */}
      {createTypeDialogOpen && (
        <Dialog
          open={createTypeDialogOpen}
          onClose={() => setCreateTypeDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Activity Type</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Activity Type"
                required
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={createTypeForm.type_name}
                onChange={(e) =>
                  setCreateTypeForm({ ...createTypeForm, type_name: e.target.value })
                }
              />
              <TextField
                select
                margin="normal"
                label="Active"
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
                value={createTypeForm.is_active}
                onChange={(e) =>
                  setCreateTypeForm({ ...createTypeForm, is_active: Number(e.target.value) })
                }
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateTypeDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateTypeSubmit}
              sx={{ ml: 2, color: "#fff", fontWeight: 500, letterSpacing: 1 }}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* Edit Activity Type Dialog */}
      {editTypeDialogOpen && (
        <Dialog
          open={editTypeDialogOpen}
          onClose={() => setEditTypeDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Activity Type</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Activity Type"
                required
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editTypeForm.type_name}
                onChange={(e) => setEditTypeForm({ ...editTypeForm, type_name: e.target.value })}
              />
              <TextField
                select
                margin="normal"
                label="Active"
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
                value={editTypeForm.is_active}
                onChange={(e) =>
                  setEditTypeForm({ ...editTypeForm, is_active: Number(e.target.value) })
                }
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditTypeDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleEditTypeSubmit}
              sx={{ ml: 2, color: "#fff", fontWeight: 500, letterSpacing: 1 }}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {typesDialogOpen && openTypesFor && (
        <Dialog open={typesDialogOpen} onClose={handleCloseTypesDialog} maxWidth="md" fullWidth>
          <DialogTitle>{`Types for ${openTypesFor.activity_category}`}</DialogTitle>
          <DialogContent>
            <Box mt={1}>
              <DataGrid
                autoHeight
                rows={typesRows}
                columns={typesColumns}
                loading={typesLoading}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                disableRowSelectionOnClick
                sx={{ minHeight: 300 }}
                getRowId={(row) => row.activity_type_id}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseTypesDialog}
              variant="outlined"
              sx={{
                color: "#fff",
                backgroundColor: "#1976d2",
                borderColor: "#1976d2",
                fontWeight: 500,
                letterSpacing: 1,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Snackbar
        open={typesSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setTypesSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setTypesSnackbarOpen(false)}
          severity={typesSnackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {typesSnackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
